import React from "react";
import { Stack, Typography, Box } from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";
import { ModalV2 } from "@/components";
import { useDeleteDutyColumnMutation } from "@/services/product-catalogue/duty-columns-api";
import { DutyColumn } from "@/services/product-catalogue/duty-columns-api";

interface DeleteDutyColumnDialogProps {
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
  column: DutyColumn | null;
}

const DeleteDutyColumnDialog: React.FC<DeleteDutyColumnDialogProps> = ({
  open,
  onClose,
  onDeleted,
  column,
}) => {
  const [deleteDutyColumn, { isLoading }] = useDeleteDutyColumnMutation();

  const handleDelete = async () => {
    if (!column) return;

    try {
      await deleteDutyColumn(column._id).unwrap();
      onDeleted();
    } catch (error) {
      console.error("Failed to delete duty column:", error);
    }
  };

  if (!column) return null;

  return (
    <ModalV2
      show={open}
      setShow={() => onClose()}
      title="Delete Duty Column"
      subTitle="This action cannot be undone"
      buttonText="Delete Column"
      buttonAction={handleDelete}
      isConfirmButtonLoading={isLoading}
      maxWidth="sm"
      modalStyles={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box sx={{ 
            p: 3, 
            borderRadius: '12px', 
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
          }}>
            <Typography variant="body1" color="#374151" sx={{ mb: 2 }}>
              Are you sure you want to delete this duty column? This will permanently remove:
            </Typography>
            
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
            }}>
              <Stack spacing={1}>
                <Typography variant="body2" color="#6b7280">
                  <strong>Column Name:</strong> {column.name}
                </Typography>
                <Typography variant="body2" color="#6b7280">
                  <strong>Description:</strong> {column.description || 'N/A'}
                </Typography>
                <Typography variant="body2" color="#6b7280">
                  <strong>Number of Rules:</strong> {column.rules?.length || 0}
                </Typography>
                <Typography variant="body2" color="#6b7280">
                  <strong>Status:</strong> {column.isActive ? 'Active' : 'Inactive'}
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Box sx={{ 
            p: 3, 
            borderRadius: '12px', 
            backgroundColor: '#fffbeb',
            border: '1px solid #fed7aa',
          }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <WarningIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
              <Typography variant="body2" color="#92400e" fontWeight={500}>
                This action is permanent and cannot be undone. All associated rules and tariff calculations will be lost.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </ModalV2>
  );
};

export default DeleteDutyColumnDialog; 